import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast, Toaster } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import HomeBusinessCard from "@/components/HomeBusinessCard";
import SeasonalDecor from "@/components/SeasonalDecor";
import CategoryTypeahead from "@/components/CategoryTypeahead";
import MyBoardDemo from "@/components/MyBoardDemo";
import { trackEvent } from "@/lib/analytics";
import {
  Search, MapPin, Briefcase, Sparkles, ChevronDown, MessageCircle, MessageSquare,
  ShieldCheck, Star, Bookmark, BadgeCheck, IdCard, ArrowRight, Plus, Users,
  Hammer, Leaf, Zap, PaintRoller, MoreHorizontal, Camera, SprayCan,
  LayoutGrid, Check,
} from "lucide-react";

const HERO_IMG = "https://static.prod-images.emergentagent.com/jobs/e7e866a1-f6d8-443c-9a1c-b532870e354d/images/ebac7b072eb94214ac85178e09652b5b45a3ecdbd26d83e66b013c6051cb621e.jpeg";
const BANNER_WOOD = "/banner-wood.jpg";

// Curated display groups for the "Browse by category" strip. `filter` maps to a
// real backend category value where one exists; groups without an exact match
// (e.g. "Home Improvement") fall back to a plain, unfiltered /search link.
const CATEGORY_GROUPS = [
  { label: "Home Improvement", icon: Hammer, filter: null, color: "#64748B" },
  { label: "Outdoor & Lawn Care", icon: Leaf, filter: "Landscaping", color: "#6E8B5B" },
  { label: "Electrical & Lighting", icon: Zap, filter: "Electrical", color: "#C39A3A" },
  { label: "Painting & Decor", icon: PaintRoller, filter: "Painting", color: "#C2734B" },
  { label: "Cleaning & Organizing", icon: SprayCan, filter: "Cleaning", color: "#4F8C88" },
];


const TRUST_ROW = [
  { icon: Users, label: "Neighbor recommended" },
  { icon: MessageSquare, label: "Direct messaging" },
  { icon: Camera, label: "Local portfolios" },
];

// Friendly words that glide across the welcome marquee (kept distinct, no claims).
const WELCOME_WORDS = [
  "Welcome to the neighborhood",
  "Find someone you can trust",
  "Save the ones you love",
  "Come back when you're ready",
  "Recommend the good ones",
  "Built with your community",
];

export default function Home() {
  const { workedWith, user } = useAuth();
  const [showcase, setShowcase] = useState({ pros: [], slots: 4 });
  const [zip, setZip] = useState("");
  const [zipInput, setZipInput] = useState("");
  const [needsLocation, setNeedsLocation] = useState(true);
  const [zipError, setZipError] = useState("");
  const [q, setQ] = useState("");
  const [city, setCity] = useState("");
  const [category, setCategory] = useState("");
  const [catSuggest, setCatSuggest] = useState(null); // { query, reason, sending }
  const [meta, setMeta] = useState({ categories: [], cities: [] });
  const [season, setSeason] = useState("none");
  const navigate = useNavigate();

  const loadShowcase = (z) => {
    api.get("/pros/showcase", { params: z ? { zip: z } : {} }).then(r => {
      setShowcase(r.data);
      setNeedsLocation(!!r.data.needs_location);
    }).catch(() => {});
  };

  useEffect(() => {
    api.get("/pros/categories").then(r => setMeta(r.data));
    api.get("/config/public").then(r => setSeason(r.data.home_season || "none")).catch(() => {});
  }, []);

  // Resolve location: saved profile ZIP first (logged-in), else the shared
  // localStorage ZIP (synced with The Wall). Never guess for a fresh visitor.
  useEffect(() => {
    const z = (user?.zip || localStorage.getItem("lima_zip") || "").trim();
    setZip(z); setZipInput(z);
    loadShowcase(z);
    // eslint-disable-next-line
  }, [user]);

  const applyZip = (e) => {
    e?.preventDefault?.();
    const z = (zipInput || "").trim();
    if (!/^\d{5}$/.test(z)) { setZipError("Please enter a 5-digit ZIP code."); return; }
    setZipError("");
    api.get("/locations/validate", { params: { zip: z } }).then(r => {
      if (!r.data.valid) { setZipError(r.data.message || "Please enter a valid U.S. ZIP code."); return; }
      localStorage.setItem("lima_zip", z);
      trackEvent("zip_search", z);
      setZip(z);
      loadShowcase(z);
    }).catch(() => setZipError("Could not verify that ZIP code. Please try again."));
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (city) params.set("city", city);
    if (category) params.set("category", category);
    trackEvent("search_submit", q || category || "");
    navigate(`/search?${params.toString()}`);
  };

  const submitCategorySuggestion = async () => {
    if (!catSuggest) return;
    setCatSuggest(s => ({ ...s, sending: true }));
    try {
      await api.post("/categories/suggest", { suggested_category: catSuggest.query, reason: catSuggest.reason });
      toast.success("Thanks — sent to the LIMYA team for review.");
      setCatSuggest(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send suggestion");
      setCatSuggest(s => ({ ...s, sending: false }));
    }
  };

  return (
    <div className="bg-[#F5F7F5] min-h-screen">
      <Header />
      <Toaster position="top-center"/>

      {/* HERO — full-width forest-green band, centered */}
      <section className="relative z-20 bg-[#20493B] px-6 pt-16 pb-14 text-center overflow-hidden">
        <div className="max-w-3xl mx-auto fade-up relative z-10">
          <p className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 border border-white/20 text-[#EAF3EE] text-xs font-semibold tracking-wide uppercase mb-6">
            <Sparkles size={12} className="text-[#9FD3BE]"/> Free to browse · No account needed
          </p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-5 text-[#F5F7F5]" style={{fontFamily:'Outfit'}}>
            The neighborhood bulletin board, <span className="text-[#9FD3BE]">reimagined.</span>
          </h1>
          <p className="text-base sm:text-lg text-[#CFE3D8] max-w-xl mx-auto mb-8">
            Discover trusted local pros your neighbors already use — save them to your board and come back when you're ready to hire or recommend.
          </p>

          {/* SEARCH — 3 working parts preserved (keyword + city + service typeahead) */}
          <form onSubmit={handleSearch} className="bg-white rounded-2xl shadow-[0_20px_50px_rgba(0,0,0,0.28)] p-2 flex flex-col sm:flex-row items-stretch gap-2 text-left" data-testid="home-search-form">
            <div className="flex items-center gap-2 flex-1 min-w-0 px-4 py-2.5">
              <Search size={20} className="text-[#2E6B57] shrink-0"/>
              <input
                type="text" placeholder="Search for a pro (e.g. plumber, electrician, cleaner…)"
                value={q} onChange={(e) => setQ(e.target.value)}
                className="flex-1 min-w-0 outline-none bg-transparent text-[15px]"
                data-testid="home-search-query"
              />
            </div>
            <div className="flex items-center gap-2 shrink-0 sm:w-36 px-3 py-2.5 sm:border-l border-[#2D2926]/10 relative">
              <MapPin size={16} className="text-[#2D2926]/40 shrink-0"/>
              <select value={city} onChange={(e) => setCity(e.target.value)} className="flex-1 min-w-0 outline-none bg-transparent text-sm appearance-none cursor-pointer" data-testid="home-search-city">
                <option value="">Any city</option>
                {meta.cities.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
              <ChevronDown size={14} className="text-[#2D2926]/40 shrink-0"/>
            </div>
            <div className="flex items-center gap-2 shrink-0 sm:w-40 px-3 py-2.5 sm:border-l border-[#2D2926]/10">
              <Briefcase size={16} className="text-[#2D2926]/40 shrink-0"/>
              <CategoryTypeahead
                value={category}
                onChange={setCategory}
                onSelect={(name) => { setCategory(name); setCatSuggest(null); }}
                onNotFound={(q2) => setCatSuggest({ query: q2, reason: "", sending: false })}
                placeholder="All services"
                testId="home-search-category"
                inputClassName="flex-1 min-w-0 outline-none bg-transparent text-sm"
              />
            </div>
            <button type="submit" className="btn-lime px-7 py-3 shrink-0 whitespace-nowrap" data-testid="home-search-submit">Search</button>
          </form>

          {catSuggest && (
            <div className="mt-3 bg-white border border-[#2D2926]/10 rounded-xl p-3.5 flex flex-wrap items-center gap-3 text-left" data-testid="home-suggest-category-panel">
              {user ? (
                <>
                  <span className="text-sm text-[#2D2926]/70">Can't find <b>&ldquo;{catSuggest.query}&rdquo;</b>?</span>
                  <input value={catSuggest.reason} onChange={e => setCatSuggest(s => ({ ...s, reason: e.target.value }))}
                    placeholder="Why is this needed? (optional)"
                    className="px-3 py-1.5 border border-black/10 rounded-lg text-sm flex-1 min-w-[160px] outline-none focus:ring-2 focus:ring-[#2E6B57]"
                    data-testid="home-suggest-reason-input"/>
                  <button onClick={submitCategorySuggestion} disabled={catSuggest.sending}
                    className="text-xs px-4 py-1.5 rounded-full bg-[#222] text-white font-semibold disabled:opacity-50 shrink-0" data-testid="home-suggest-submit">
                    {catSuggest.sending ? "Sending…" : "Send suggestion"}
                  </button>
                </>
              ) : (
                <span className="text-sm text-[#2D2926]/70">Can't find <b>&ldquo;{catSuggest.query}&rdquo;</b>? <Link to="/login" className="text-[#245544] font-semibold underline" data-testid="home-suggest-login-link">Sign in</Link> to suggest it.</span>
              )}
              <button onClick={() => setCatSuggest(null)} className="text-[#2D2926]/40 hover:text-[#2D2926] shrink-0" data-testid="home-suggest-dismiss">×</button>
            </div>
          )}

          {/* Trust tagline — carries the trust-row info in the reference's centered style */}
          <div className="mt-7 flex items-center justify-center flex-wrap gap-x-3 gap-y-1 text-[11px] sm:text-xs font-semibold uppercase tracking-[0.16em] text-[#9FD3BE]" data-testid="home-trust-row">
            {TRUST_ROW.map(({ label }, i) => (
              <span key={label} className="inline-flex items-center gap-3" data-testid={`trust-row-item-${i}`}>
                {i > 0 && <span className="opacity-40">|</span>}
                {label}
              </span>
            ))}
          </div>

          {/* Public beta note — sits directly above the Featured Pros board */}
          <p className="mt-8 text-sm text-[#CFE3D8]/85 max-w-xl mx-auto" data-testid="home-beta-note">
            <span className="font-semibold text-[#EAF3EE]">LIMYA is currently in public beta.</span>{" "}
            We're building it with our community and would love your feedback.{" "}
            <Link to="/feedback" className="underline decoration-[#E0A83E] decoration-2 underline-offset-2 text-[#F5F7F5] font-semibold hover:text-[#E0A83E] transition-colors" data-testid="home-beta-feedback-link">Share feedback</Link>
            <span className="block mt-1 text-[#CFE3D8]/70 text-xs">Pro Plus features coming soon ✨</span>
          </p>
        </div>
      </section>

      {/* MY BOARD TEASER — full-width strip bridging hero into Featured Pros */}
      <section className="bg-[#E5F0E9] border-b border-[#CDE3D7]" data-testid="myboard-teaser-strip">
        <div className="max-w-7xl mx-auto px-6 py-3.5 flex flex-wrap items-center justify-center sm:justify-between gap-x-5 gap-y-2">
          <div className="flex items-center gap-3 text-[#20493B] min-w-0">
            <span className="w-8 h-8 rounded-lg bg-[#20493B] grid place-items-center shrink-0">
              <LayoutGrid size={16} className="text-[#9FD3BE]"/>
            </span>
            <p className="font-semibold text-sm sm:text-[15px] leading-snug" style={{fontFamily:'Outfit'}}>
              Save the pros you like to your board — and sort them into project folders.
            </p>
          </div>
          <Link to="/learn" className="shrink-0 inline-flex items-center gap-1.5 text-[#245544] font-semibold text-sm underline underline-offset-2 hover:gap-2.5 transition-all" data-testid="myboard-teaser-link">
            See how My Board works <ArrowRight size={16}/>
          </Link>
        </div>
      </section>

      {/* FEATURED PROS — framed corkboard */}
      <section className="relative z-10 px-6 pt-10 pb-16">
        <div className="max-w-7xl mx-auto">
          <div className="wood-frame">
          <div className="relative cork-board rounded-[24px] p-6 sm:p-8 overflow-hidden" style={{ background: "radial-gradient(circle at 20% 20%, #dcc09a, #cdab80 60%, #bd9668)" }}>
            <SeasonalDecor season={season} />

            {/* Header: title + "See all pros" link to browse everyone */}
            <div className="relative z-10 mb-4 flex items-start justify-between gap-4">
              <div>
                <h2 className="text-2xl sm:text-3xl font-bold flex items-center gap-2 text-[#2D2926]" style={{fontFamily:'Outfit'}}>
                  <Sparkles className="text-[#2E6B57]" size={22}/>
                  Featured Pros
                </h2>
                <p className="text-xs uppercase tracking-[0.2em] text-[#5C554E] mt-1">Featured by your neighborhood</p>
              </div>
              <button
                onClick={() => navigate("/search")}
                className="shrink-0 inline-flex items-center gap-1.5 bg-white text-[#2E6B57] font-semibold text-sm rounded-full pl-4 pr-3.5 py-2 shadow-md hover:shadow-lg hover:gap-2.5 transition-all"
                data-testid="board-see-all-pros"
              >
                See all pros <ArrowRight size={16}/>
              </button>
            </div>

            {/* ZIP entry / current location — styled as a paper note pinned to the board */}
            <div className="relative z-10 mb-6" data-testid="showcase-location-bar">
              {needsLocation ? (
                <div className="relative inline-block">
                  <span className="push-pin" style={{ "--pin": "#2E6B57" }} />
                  <form onSubmit={applyZip} className="bg-[#FFFDF6] rounded-xl shadow-[0_8px_20px_rgba(60,40,20,0.25)] border border-black/5 -rotate-1 px-4 pt-4 pb-3 flex flex-wrap items-center gap-x-2.5 gap-y-2" data-testid="showcase-location-prompt">
                    <span className="text-xs font-bold uppercase tracking-wider text-[#2D2926]">Local pros</span>
                    <span className="text-xs text-[#2D2926]/60">· add your ZIP to see who's near you</span>
                    <span className="flex items-center gap-1.5 bg-[#F2F4F2] rounded-full pl-3 pr-1 py-1 ml-1">
                      <MapPin size={14} className="text-[#2D2926]/45 shrink-0"/>
                      <input
                        value={zipInput} onChange={e => { setZipInput(e.target.value.replace(/\D/g, "")); setZipError(""); }}
                        maxLength={5} inputMode="numeric" placeholder="ZIP" aria-label="Your ZIP code"
                        className="outline-none bg-transparent text-sm w-16 py-1 text-[#2D2926] placeholder:text-[#2D2926]/40"
                        data-testid="showcase-zip-input"
                      />
                      <button type="submit" className="btn-lime px-3.5 py-1.5 text-sm shrink-0" data-testid="showcase-zip-submit">Go</button>
                    </span>
                    {zipError && <p className="w-full text-xs text-red-500" data-testid="showcase-zip-error">{zipError}</p>}
                  </form>
                </div>
              ) : (
                <div className="relative inline-block">
                  <span className="push-pin" style={{ "--pin": "#2E6B57" }} />
                  <div className="bg-[#FFFDF6] rounded-xl shadow-[0_8px_20px_rgba(60,40,20,0.25)] border border-black/5 -rotate-1 px-4 pt-4 pb-3 flex items-center gap-2 text-sm text-[#2D2926]">
                    <MapPin size={14} className="text-[#2E6B57]"/>
                    <span>Showing pros near <span className="font-semibold">{zip}</span></span>
                    <button onClick={() => setNeedsLocation(true)} className="text-[#2E6B57] underline underline-offset-2 font-semibold hover:opacity-80" data-testid="showcase-change-location">Change</button>
                  </div>
                </div>
              )}
            </div>

            {/* The board always shows 4 cards: eligible local pros first, then
                universal EXAMPLE cards fill the rest. "This could be you" only
                appears if neither can fill a slot. */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-7 relative z-10" data-testid="pros-of-the-week">
                {showcase.pros.map((p, i) => (
                  <div key={p.user_id} className="float-card" style={{ animationDelay: `${i * 0.3}s` }}>
                    <HomeBusinessCard pro={p} index={i} workedTogether={workedWith?.has(p.user_id)}/>
                  </div>
                ))}
                {Array.from({ length: Math.max(0, showcase.slots - showcase.pros.length) }).map((_, i) => (
                  <button
                    key={`slot-${i}`}
                    onClick={() => navigate("/register?role=pro")}
                    className="float-card group rounded-2xl border-2 border-dashed border-white/50 bg-white/10 hover:bg-white/20 transition p-5 min-h-[280px] flex flex-col items-center justify-center text-center"
                    style={{ animationDelay: `${(showcase.pros.length + i) * 0.3}s` }}
                    data-testid="showcase-placeholder"
                  >
                    <div className="w-12 h-12 rounded-full bg-[#2E6B57] grid place-items-center mb-3 group-hover:scale-110 transition"><Plus size={22} className="text-[#FFFFFF]"/></div>
                    <p className="font-bold text-[#F2F4F2] text-sm" style={{fontFamily:'Outfit'}}>This could be you</p>
                    <p className="text-[11px] text-[#F2F4F2]/90 mt-1 leading-snug">Go Pro Plus & complete your profile to be featured here.</p>
                  </button>
                ))}
            </div>
          </div>
          </div>

          {/* Browse all pros — quiet secondary action */}
          <div className="text-center mt-10">
            <button
              onClick={() => navigate("/search")}
              className="inline-flex items-center justify-center gap-2 px-8 py-3 rounded-full border border-[#2D2926]/25 text-[#2D2926] bg-transparent hover:bg-[#2D2926]/5 hover:border-[#2D2926]/50 transition font-medium text-sm"
              data-testid="browse-all-pros-btn"
            >
              Browse all pros <ArrowRight size={16}/>
            </button>
          </div>
        </div>
      </section>

      {/* WELCOME MARQUEE — hidden for now (kept for later re-enable) */}
      <section className="pb-12 hidden" data-testid="welcome-marquee" aria-hidden="true">
        <div className="marquee-mask overflow-hidden bg-[#EAF3EE] border-y border-[#CDE3D7] py-3">
          <div className="marquee-track text-[#20493B]/85 text-xs sm:text-sm font-semibold uppercase tracking-[0.16em]" style={{fontFamily:'Outfit'}}>
            {Array.from({ length: 2 }).map((_, dup) => (
              <span key={dup} className="inline-flex items-center">
                {WELCOME_WORDS.map((w, i) => (
                  <span key={`${dup}-${i}`} className="inline-flex items-center">
                    <span className="px-7">{w}</span>
                    <Leaf size={13} className="text-[#2E6B57]/55 shrink-0"/>
                  </span>
                ))}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Categories strip — icon-circle cards (moved up, right after Featured Pros) */}
      <section className="px-6 pt-4 pb-16">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-3xl font-bold text-[#2D2926]" style={{fontFamily:'Outfit'}}>Browse by category</h2>
            <button onClick={() => navigate("/search")} className="text-sm font-semibold text-[#598A23] inline-flex items-center gap-1 hover:underline shrink-0" data-testid="category-view-all">
              View all <ArrowRight size={14}/>
            </button>
          </div>
          <p className="text-[#5C554E] mb-8">Whatever you need, there may be a neighbor-trusted Pro for it.</p>
          <div className="flex flex-wrap gap-x-8 gap-y-6">
            {CATEGORY_GROUPS.map(({ label, icon: Icon, filter, color }) => (
              <button
                key={label}
                onClick={() => { trackEvent("category_click", label); navigate(filter ? `/search?category=${encodeURIComponent(filter)}` : "/search"); }}
                className="flex flex-col items-center gap-2.5 w-24 group"
                data-testid={`category-pill-${label}`}
              >
                <span className="w-14 h-14 rounded-full bg-[#EEF2EF] border border-[#D3DAD5] grid place-items-center group-hover:bg-[#2D2926]/[0.06] transition">
                  <Icon size={22} style={{ color }} strokeWidth={1.8}/>
                </span>
                <span className="text-xs font-medium text-[#2D2926] text-center leading-tight">{label}</span>
              </button>
            ))}
            <button onClick={() => navigate("/search")} className="flex flex-col items-center gap-2.5 w-24 group" data-testid="category-pill-more">
              <span className="w-14 h-14 rounded-full bg-[#EEF2EF] border border-[#D3DAD5] grid place-items-center group-hover:bg-[#2D2926]/[0.06] transition">
                <MoreHorizontal size={22} className="text-[#2D2926]"/>
              </span>
              <span className="text-xs font-medium text-[#2D2926] text-center leading-tight">More Categories</span>
            </button>
          </div>
        </div>
      </section>

      {/* MY BOARD FEATURE — what makes LIMYA different */}
      <section className="px-6 pb-16" data-testid="myboard-feature">
        <div className="max-w-7xl mx-auto bg-[#EDF4EF] border border-[#D6E7DD] rounded-[28px] p-6 sm:p-10 lg:p-14 grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
          {/* LEFT — copy */}
          <div>
            <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-[#2E6B57] bg-[#DCEAE1] border border-[#C4DBCD] rounded-full px-3 py-1 mb-4" data-testid="myboard-feature-audience">
              <Users size={13}/> For neighbours
            </span>
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#2E6B57] mb-4">What makes LIMYA different</p>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#20493B] leading-tight mb-4" style={{fontFamily:'Outfit'}}>
              Save pros to your board. Sort them into project folders.
            </h2>
            <p className="text-[#4B5A52] text-base leading-relaxed mb-6 max-w-md">
              Every pro you pin lands on your own corkboard. Group them by project so the right person is one tap away when it's time to hire.
            </p>
            <ul className="space-y-3 mb-8">
              {["Pin anyone with one tap", "Group them by room or project", "Recommend the ones you love"].map((t) => (
                <li key={t} className="flex items-center gap-3 text-[#2D2926]" data-testid={`myboard-feature-bullet`}>
                  <span className="w-6 h-6 rounded-full bg-[#2E6B57] grid place-items-center shrink-0"><Check size={14} className="text-white"/></span>
                  <span className="text-sm sm:text-base font-medium">{t}</span>
                </li>
              ))}
            </ul>
            {user ? (
              <Link to="/board" className="btn-lime inline-flex items-center gap-2 px-7 py-3 text-sm" data-testid="myboard-feature-cta">
                Go to My Board <ArrowRight size={16}/>
              </Link>
            ) : (
              <Link to="/register" className="btn-lime inline-flex items-center gap-2 px-7 py-3 text-sm" data-testid="myboard-feature-cta">
                Join as a neighbour <ArrowRight size={16}/>
              </Link>
            )}
          </div>

          {/* RIGHT — animated board demo */}
          <div className="relative" data-testid="myboard-feature-preview">
            <MyBoardDemo />
          </div>
        </div>
      </section>

      {/* HOW LIMYA WORKS — removed from home; the full walkthrough lives on /learn */}

      {/* FOR PROS — no-lead-fees / pro recruitment banner on a wood board */}
      <section className="px-6 pb-16" data-testid="no-leads-banner">
        <div
          className="relative max-w-7xl mx-auto rounded-3xl overflow-hidden shadow-[0_20px_50px_rgba(45,41,38,0.20)] bg-[position:right_center] sm:bg-[position:left_center]"
          style={{ backgroundImage: `url(${BANNER_WOOD})`, backgroundSize: "cover" }}
        >
          {/* darkening overlays for text contrast (stronger on mobile, gradient to the right on desktop) */}
          <div className="absolute inset-0 bg-black/60 sm:bg-transparent" aria-hidden="true" />
          <div
            className="absolute inset-0"
            aria-hidden="true"
            style={{ background: "linear-gradient(90deg, rgba(30,22,14,0.15) 0%, rgba(30,22,14,0.30) 36%, rgba(26,19,12,0.82) 58%, rgba(24,17,11,0.9) 100%)" }}
          />
          <div className="relative flex items-stretch min-h-[190px]">
            {/* spacer keeps the pinned business card (baked into the image) visible on desktop */}
            <div className="hidden sm:block sm:w-[36%]" aria-hidden="true" />
            <div className="flex-1 p-6 sm:p-8 sm:pl-2 flex flex-col md:flex-row md:items-center gap-6">
              <div className="flex-1 text-[#F5F2EC]">
                <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-[#E7C9A0] mb-2">For professionals</p>
                <h3 className="text-xl sm:text-2xl font-bold uppercase tracking-wide leading-tight" style={{fontFamily:'Outfit'}}>
                  LIMYA never charges<br className="hidden sm:block"/> for leads
                </h3>
                <p className="text-sm text-[#F5F2EC]/85 mt-2 max-w-md leading-relaxed">
                  Neighbors browse, save, recommend, and message pros directly.
                </p>
              </div>
              <div className="shrink-0 flex flex-col items-start md:items-center gap-2">
                <button
                  onClick={() => navigate("/register?role=pro")}
                  className="bg-white text-[#2D2926] font-bold rounded-full px-6 py-3 inline-flex items-center gap-2 hover:bg-[#F2F4F2] transition whitespace-nowrap shadow-lg"
                  data-testid="banner-join-pro-btn"
                >
                  Join LIMYA as a Pro <ArrowRight size={16}/>
                </button>
                <span className="inline-flex items-center gap-1.5 text-xs text-[#F5F2EC]/85" data-testid="banner-free-to-join">
                  <BadgeCheck size={14} className="text-[#7FD1A8]"/> Free to join
                </span>
                <button
                  onClick={() => navigate("/upgrade")}
                  className="text-sm text-[#F5F2EC] underline underline-offset-2 hover:opacity-80 inline-flex items-center gap-1"
                  data-testid="banner-pro-plus-link"
                >
                  See Pro Plus perks <ArrowRight size={14}/>
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Categories strip — moved above the My Board feature (see below) */}

      <Footer />
    </div>
  );
}
